import React, { useState, useRef } from 'react';
import { Plus, Edit2, Trash2, Image, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LogoModal = ({ logo, onSave, onClose }) => {
  const [form, setForm] = useState(logo || {
    name: '', image_url: '', link: '', order: 0, visible: true
  });
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await uploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await uploadFile(e.target.files[0]);
    }
  };

  const uploadFile = async (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (PNG, JPG, GIF, SVG, WebP)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const password = localStorage.getItem('adminPassword');
      const response = await fetch(`${API_URL}/api/upload/logo?password=${password}`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setForm({...form, image_url: data.image_url});
        toast.success('Logo uploaded successfully');
      } else {
        toast.error('Failed to upload logo');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-[#0a1a14] border border-white/10 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg text-white mb-4">{logo ? 'Edit Affiliation' : 'Add Affiliation'}</h3>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-sm block mb-1">Name *</label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm({...form, name: e.target.value})} 
              className="bg-white/5 border-white/10 text-white" 
              placeholder="Affiliation Name"
            />
          </div>
          
          <div>
            <label className="text-white/50 text-sm block mb-1">Logo Image *</label>
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer
                ${dragActive ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/20 hover:border-white/40'}
                ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className={`w-8 h-8 mx-auto mb-2 ${dragActive ? 'text-[#D4AF37]' : 'text-white/30'}`} />
              {uploading ? (
                <p className="text-white/50 text-sm">Uploading...</p>
              ) : (
                <>
                  <p className="text-white/50 text-sm">Drag & drop logo here</p>
                  <p className="text-white/30 text-xs mt-1">or click to browse</p>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-white/30 text-xs">OR</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>

          <div>
            <label className="text-white/50 text-sm block mb-1">Image URL</label>
            <Input 
              value={form.image_url} 
              onChange={(e) => setForm({...form, image_url: e.target.value})} 
              className="bg-white/5 border-white/10 text-white" 
              placeholder="https://..."
            />
          </div>

          {form.image_url && (
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-white/40 text-xs mb-2">Preview:</p>
              <div className="flex items-center justify-center h-16 bg-white/5 rounded">
                <img 
                  src={form.image_url} 
                  alt="Preview" 
                  className="max-h-12 max-w-full object-contain opacity-60"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-white/50 text-sm block mb-1">Link (optional)</label>
            <Input 
              value={form.link} 
              onChange={(e) => setForm({...form, link: e.target.value})} 
              className="bg-white/5 border-white/10 text-white" 
              placeholder="https://affiliation-website.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm block mb-1">Display Order</label>
              <Input 
                type="number"
                value={form.order} 
                onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} 
                className="bg-white/5 border-white/10 text-white" 
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-1">Visibility</label>
              <button
                type="button"
                onClick={() => setForm({...form, visible: !form.visible})}
                className={`w-full h-10 rounded-md border transition-colors flex items-center justify-center gap-2 text-sm
                  ${form.visible 
                    ? 'bg-green-500/20 border-green-500/40 text-green-400' 
                    : 'bg-white/5 border-white/10 text-white/40'}`}
              >
                {form.visible ? (
                  <><CheckCircle className="w-4 h-4" /> Visible</>
                ) : (
                  <><AlertCircle className="w-4 h-4" /> Hidden</>
                )}
              </button>
            </div>
          </div>
          <p className="text-white/30 text-xs">Lower order numbers appear first. Hidden logos won't show on the website.</p>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button 
            onClick={() => onSave({...form, id: logo?.id})} 
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            disabled={!form.name || !form.image_url || uploading}
          >
            {logo ? 'Update' : 'Add'} Logo
          </Button>
        </div>
      </div>
    </div>
  );
};

export const LogosTab = ({ logos, saveLogo, deleteLogo }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg text-white">Affiliations</h3>
            <p className="text-white/40 text-sm">Logos displayed in a subtle strip above the footer on all pages</p>
          </div>
          <Button 
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            data-testid="add-logo-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Logo
          </Button>
        </div>
        {logos.length === 0 ? (
          <div className="text-center text-white/50 py-8">
            <Image className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No affiliations yet.</p>
            <p className="text-sm mt-1">Add logos to display them in the Affiliations strip above the footer.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {logos.map((logo) => (
              <div 
                key={logo.id} 
                className={`bg-white/5 border rounded-lg p-4 group relative
                  ${logo.visible === false ? 'border-white/5 opacity-50' : 'border-white/10'}`}
              >
                {logo.visible === false && (
                  <div className="absolute top-2 right-2 bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded">
                    Hidden
                  </div>
                )}
                <div className="aspect-[3/2] flex items-center justify-center bg-white/5 rounded mb-3">
                  <img 
                    src={logo.image_url} 
                    alt={logo.name}
                    className="max-h-12 max-w-full object-contain opacity-60"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{logo.name}</p>
                    <p className="text-white/30 text-xs">Order: {logo.order}</p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                    <button onClick={() => { setEditing(logo); setShowModal(true); }} className="p-1 hover:bg-white/10 rounded">
                      <Edit2 className="w-3 h-3 text-white/60" />
                    </button>
                    <button onClick={() => deleteLogo(logo.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <LogoModal
          logo={editing}
          onSave={(l) => { saveLogo(l); setShowModal(false); setEditing(null); }}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </>
  );
};
